import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Search, Trash2, RefreshCw, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Owner-only admin tab , every newsletter subscriber.
 *
 * Pulls from `GET /api/admin/newsletter/subscribers` with optional
 * search + status filters. Lets the owner:
 *   - search by email (substring, case-insensitive)
 *   - filter by status (all / active / unsubscribed)
 *   - export the full list to CSV (download)
 *   - hard-delete a row (GDPR right-to-erasure, or just tidy-up)
 *
 * All three privileged endpoints are gated by `verify_owner` server-
 * side so a non-owner admin gets a clean 403 even if they reach this
 * tab somehow.
 */
export default function SubscribersTab({ getAuthHeaders }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [confirmingId, setConfirmingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (status !== "all") params.status_filter = status;
      const { data } = await axios.get(`${API}/admin/newsletter/subscribers`, {
        params,
        headers: getAuthHeaders(),
      });
      setRows(data.items || []);
      setTotal(data.total || 0);
      setActive(data.active || 0);
    } catch (err) {
      const msg =
        err?.response?.status === 403
          ? "Owner-only , this tab is reserved for the account owner."
          : "Couldn't load the subscriber list. Try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Initial + filter-driven loads. The search box debounces lightly so
  // we're not firing a request on every keystroke.
  useEffect(() => {
    const t = setTimeout(load, search.trim() ? 250 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status]);

  const onDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/newsletter/subscribers/${id}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Subscriber removed.");
      setConfirmingId(null);
      load();
    } catch {
      toast.error("Couldn't remove that subscriber.");
    }
  };

  const onExport = async () => {
    try {
      const res = await axios.get(
        `${API}/admin/newsletter/subscribers/export.csv`,
        { headers: getAuthHeaders(), responseType: "blob" },
      );
      // Trigger a clean download from the blob response
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `meditalk-subscribers-${new Date()
        .toISOString()
        .slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported.");
    } catch (err) {
      const msg =
        err?.response?.status === 403
          ? "Owner-only , this export is reserved for the account owner."
          : "Couldn't export. Try again.";
      toast.error(msg);
    }
  };

  const inactiveCount = useMemo(() => total - active, [total, active]);

  return (
    <div data-testid="admin-subscribers-tab">
      {/* Header strip , title + KPIs + action buttons */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <h2 className="font-['Outfit'] font-bold text-2xl md:text-3xl text-[var(--charcoal)]">
            Newsletter subscribers
          </h2>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            People who've signed up via the footer form to hear about
            new studies. Owner-only view.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-2 h-10 px-3 rounded-lg border border-[var(--light-border)] text-sm font-semibold text-[var(--charcoal)] hover:bg-[var(--bone-soft)] transition-colors"
            data-testid="subscribers-refresh"
          >
            <RefreshCw size={14} />
            Refresh
          </button>
          <button
            type="button"
            onClick={onExport}
            className="inline-flex items-center gap-2 h-10 px-3 rounded-lg bg-[var(--charcoal)] text-white text-sm font-semibold hover:bg-[var(--dark-bg)] transition-colors"
            data-testid="subscribers-export"
          >
            <Download size={14} />
            Export CSV
          </button>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <Kpi label="Total" value={total} testId="subscribers-kpi-total" />
        <Kpi label="Active" value={active} accent testId="subscribers-kpi-active" />
        <Kpi
          label="Unsubscribed"
          value={inactiveCount}
          testId="subscribers-kpi-inactive"
        />
      </div>

      {/* Search + status filter */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] pointer-events-none"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by email…"
            className="w-full h-10 pl-9 pr-3 rounded-lg border border-[var(--light-border)] focus:border-[var(--teal)] focus:ring-2 focus:ring-[var(--teal)]/20 text-sm"
            data-testid="subscribers-search"
          />
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="h-10 px-3 rounded-lg border border-[var(--light-border)] focus:border-[var(--teal)] focus:ring-2 focus:ring-[var(--teal)]/20 text-sm bg-white"
          data-testid="subscribers-status-filter"
        >
          <option value="all">All</option>
          <option value="active">Active only</option>
          <option value="inactive">Unsubscribed only</option>
        </select>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-[var(--light-border)] overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="subscribers-table">
            <thead className="bg-[var(--bone-soft)] text-[var(--text-muted)] text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Email</th>
                <th className="text-left px-4 py-3 font-semibold">Source</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold">Joined</th>
                <th className="text-right px-4 py-3 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-[var(--text-muted)]">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-[var(--text-muted)]">
                    {search || status !== "all"
                      ? "No subscribers match those filters."
                      : "No newsletter subscribers yet."}
                  </td>
                </tr>
              )}
              {!loading &&
                rows.map((r) => (
                  <tr
                    key={r.id}
                    className="border-t border-[var(--light-border)] hover:bg-[var(--bone-soft)]/40"
                    data-testid={`subscriber-row-${r.id}`}
                  >
                    <td className="px-4 py-3 text-[var(--charcoal)] font-medium break-all">
                      <a
                        href={`mailto:${r.email}`}
                        className="inline-flex items-center gap-1.5 hover:text-[var(--teal-dark)]"
                      >
                        <Mail size={13} className="text-[var(--text-muted)]" />
                        {r.email}
                      </a>
                    </td>
                    <td className="px-4 py-3 text-[var(--text-muted)] whitespace-nowrap">
                      {r.source || "—"}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                          r.is_active
                            ? "bg-[var(--lime)]/20 text-[var(--charcoal)]"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {r.is_active ? "Active" : "Unsubscribed"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[var(--text-muted)] whitespace-nowrap">
                      {formatJoinedDate(r.created_at)}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {confirmingId === r.id ? (
                        <span className="inline-flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => onDelete(r.id)}
                            className="text-xs font-bold text-red-700 hover:text-red-900"
                            data-testid={`subscriber-delete-confirm-${r.id}`}
                          >
                            Confirm
                          </button>
                          <span className="text-[var(--text-muted)]">·</span>
                          <button
                            type="button"
                            onClick={() => setConfirmingId(null)}
                            className="text-xs font-semibold text-[var(--text-muted)] hover:text-[var(--charcoal)]"
                          >
                            Cancel
                          </button>
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setConfirmingId(r.id)}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--text-muted)] hover:text-red-700"
                          data-testid={`subscriber-delete-${r.id}`}
                          title="Permanently remove this subscriber"
                        >
                          <Trash2 size={12} />
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const Kpi = ({ label, value, accent, testId }) => (
  <div
    className={`rounded-xl border p-4 ${
      accent
        ? "border-[var(--lime)]/40 bg-[var(--lime)]/[0.08]"
        : "border-[var(--light-border)] bg-white"
    }`}
    data-testid={testId}
  >
    <div className="text-[11px] uppercase tracking-wider font-semibold text-[var(--text-muted)] mb-1">
      {label}
    </div>
    <div className="text-2xl font-bold font-['Outfit'] text-[var(--charcoal)]">
      {value}
    </div>
  </div>
);

function formatJoinedDate(raw) {
  if (!raw) return "—";
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return raw;
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return raw;
  }
}
